// Artifact readers, dispatched by kind. Every reader is additive decoration
// over the raw markdown - the "View raw" toggle and the DefaultReader are the
// degradation path that must always work.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  ArtifactResponse,
  ParsedClaim,
  ParsedSection,
  ParsedVerdict,
  RunDetail,
  VerdictNorm,
} from "@ideacrunch/shared";
import { useArtifact } from "../api";
import { useRunContext } from "./RunView";
import { ClaimCard } from "../components/ClaimCard";
import {
  LongText,
  MarkdownView,
  sectionText,
  StatTile,
  VerdictChip,
} from "../components/bits";
import { KIND_TITLES, useVocab } from "../vocab";

export function ArtifactView() {
  const run = useRunContext();
  const params = useParams();
  const relPath = params["*"] ?? "";
  const { data, isLoading, error } = useArtifact(run.id, relPath);

  const reviewPath =
    run.artifacts.find((a) => a.kind === "skeptic-review")?.relPath ?? "";
  const review = useArtifact(run.id, reviewPath);

  const [showRaw, setShowRaw] = useState(false);
  const { ownerMode } = useVocab();

  if (isLoading) return <div className="loading">Loading artifact...</div>;
  if (error || !data) {
    return <div className="error-box">Artifact not found: {relPath}</div>;
  }

  const kind = data.meta.kind;
  const titles = KIND_TITLES[kind];
  const heading = titles
    ? ownerMode
      ? titles.owner
      : titles.user
    : relPath;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0 }}>{heading}</h2>
        <span className="artifact-caption">
          {relPath}{" "}
          <button
            className="chip ghost clickable"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? "structured view" : "view raw"}
          </button>
        </span>
      </div>
      {data.parsed.parseWarnings.length > 0 && ownerMode && (
        <div className="parse-warnings">
          {data.parsed.parseWarnings.join(" · ")}
        </div>
      )}
      {showRaw ? (
        <div className="panel">
          <MarkdownView text={data.raw} />
        </div>
      ) : (
        <ReaderFor artifact={data} run={run} review={review.data ?? null} />
      )}
    </div>
  );
}

function ReaderFor({
  artifact,
  run,
  review,
}: {
  artifact: ArtifactResponse;
  run: RunDetail;
  review: ArtifactResponse | null;
}) {
  switch (artifact.meta.kind) {
    case "research":
      return <LaneReader artifact={artifact} review={review} />;
    case "skeptic-review":
    case "plan-review":
      return <ReviewReader artifact={artifact} run={run} />;
    case "recommendation":
      return <RecommendationReader artifact={artifact} run={run} />;
    case "intake":
      return <IntakeReader artifact={artifact} run={run} />;
    case "implementation-plan":
      return <RoadmapReader artifact={artifact} />;
    case "next-steps":
      return <NextStepsReader artifact={artifact} run={run} />;
    case "residue":
      return <DebriefReader artifact={artifact} run={run} />;
    default:
      return (
        <div className="panel">
          <MarkdownView text={artifact.raw} />
        </div>
      );
  }
}

// ---------- research lane ----------

type VerdictFilter = "all" | VerdictNorm | "ungraded" | "assumption";

function LaneReader({
  artifact,
  review,
}: {
  artifact: ArtifactResponse;
  review: ArtifactResponse | null;
}) {
  const [filter, setFilter] = useState<VerdictFilter>("all");
  const lane = /research\/([^/]+)\.md$/.exec(artifact.meta.relPath)?.[1] ?? "";

  // ref -> latest verdict (re-verdicts supersede pass 1 for display)
  const { verdictByOrdinal, refByOrdinal } = useMemo(() => {
    const verdictByRef = new Map<string, ParsedVerdict>();
    if (review) {
      for (const v of review.parsed.verdicts) {
        if (!v.isReverdict) verdictByRef.set(v.claimRef, v);
      }
      for (const v of review.parsed.verdicts) {
        if (v.isReverdict) verdictByRef.set(v.claimRef, v);
      }
    }
    const verdictByOrdinal = new Map<number, ParsedVerdict>();
    const refByOrdinal = new Map<number, string>();
    for (const link of review?.links ?? []) {
      if (link.lane === lane && link.claimOrdinal !== null) {
        const v = verdictByRef.get(link.claimRef);
        if (v) verdictByOrdinal.set(link.claimOrdinal, v);
        refByOrdinal.set(link.claimOrdinal, link.claimRef);
      }
    }
    return { verdictByOrdinal, refByOrdinal };
  }, [review, lane]);

  const claims = artifact.parsed.claims;
  const graded = claims.filter(
    (c) =>
      !c.isAssumption &&
      c.inRevision === null &&
      verdictByOrdinal.has(c.ordinal),
  ).length;

  const matches = (c: ParsedClaim): boolean => {
    if (filter === "all") return true;
    if (filter === "assumption") return c.isAssumption;
    const v =
      c.inRevision === null ? verdictByOrdinal.get(c.ordinal) : undefined;
    if (filter === "ungraded") return !c.isAssumption && v === undefined;
    return v?.verdictNorm === filter;
  };

  // Group claims under the deepest section containing them.
  const groups = useMemo(() => {
    const sections = artifact.parsed.sections.filter((s) => s.depth >= 2);
    const out: { section: ParsedSection | null; claims: ParsedClaim[] }[] = [];
    for (const claim of claims) {
      let best: ParsedSection | null = null;
      for (const s of sections) {
        if (claim.line >= s.lineStart && claim.line <= s.lineEnd) {
          if (!best || s.depth > best.depth) best = s;
        }
      }
      const last = out[out.length - 1];
      if (last && last.section?.heading === best?.heading) {
        last.claims.push(claim);
      } else {
        out.push({ section: best, claims: [claim] });
      }
    }
    return out;
  }, [artifact, claims]);

  const filterChips: { key: VerdictFilter; label: string }[] = [
    { key: "all", label: `All (${claims.length})` },
    { key: "SUPPORTED", label: "Held" },
    { key: "WEAK", label: "Thin" },
    { key: "REFUTED", label: "Broken" },
    { key: "ungraded", label: "Not graded" },
    { key: "assumption", label: "Assumptions" },
  ];

  return (
    <div>
      <div className="stat-row">
        <StatTile value={claims.filter((c) => !c.isAssumption).length} label="claims" />
        <StatTile value={graded} label="graded by the Skeptic" />
        <StatTile
          value={claims.filter((c) => c.isAssumption).length}
          label="labeled assumptions"
        />
        <StatTile value={artifact.parsed.sources.length} label="sources" />
      </div>
      <div className="filter-row">
        {filterChips.map((f) => (
          <button
            key={f.key}
            className={`chip clickable ${filter === f.key ? "accent" : "ghost"}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {groups.map((g, i) => {
        const visible = g.claims.filter(matches);
        if (visible.length === 0) return null;
        return (
          <div key={i}>
            {g.section && (
              <h3 style={{ margin: "18px 0 4px" }}>
                {g.section.heading}
                {g.section.revisionRound !== null && (
                  <span className="chip accent" style={{ marginLeft: 8 }}>
                    append-only revision
                  </span>
                )}
              </h3>
            )}
            {visible.map((c, j) => (
              <ClaimCard
                key={`${i}-${j}`}
                claim={c}
                verdict={
                  c.inRevision === null
                    ? (verdictByOrdinal.get(c.ordinal) ?? null)
                    : null
                }
                claimRef={
                  c.inRevision === null
                    ? (refByOrdinal.get(c.ordinal) ?? null)
                    : null
                }
                sources={artifact.parsed.sources}
              />
            ))}
          </div>
        );
      })}

      <details className="section-acc" style={{ marginTop: 20 }}>
        <summary>Sources ({artifact.parsed.sources.length})</summary>
        <ul>
          {artifact.parsed.sources.map((s) => (
            <li key={`${s.tag}-${s.title}`} style={{ marginBottom: 6 }}>
              <span className="chip ghost">{s.tag}</span>{" "}
              {s.url ? (
                <a href={s.url} target="_blank" rel="noreferrer">
                  {s.title}
                </a>
              ) : (
                s.title
              )}
              {s.date && (
                <span style={{ color: "var(--muted)" }}> · {s.date}</span>
              )}
              {s.note && (
                <div style={{ fontSize: 13, color: "var(--ink-2)" }}>{s.note}</div>
              )}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

// ---------- skeptic review / plan review ----------

function ReviewReader({
  artifact,
  run,
}: {
  artifact: ArtifactResponse;
  run: RunDetail;
}) {
  const [filter, setFilter] = useState<"all" | VerdictNorm>("all");
  const { steelmanTitle } = useVocab();
  const firstPass = artifact.parsed.verdicts.filter((v) => !v.isReverdict);
  const reverdicts = artifact.parsed.verdicts.filter((v) => v.isReverdict);

  const count = (n: VerdictNorm) =>
    firstPass.filter((v) => v.verdictNorm === n).length;
  const survival =
    firstPass.length > 0
      ? Math.round(
          ((count("SUPPORTED") + count("APPROVE")) / firstPass.length) * 100,
        )
      : 0;

  const planVerdict = artifact.parsed.meta["Plan verdict"];
  const registry = artifact.parsed.meta["Registry check"];
  const isPass2 = artifact.meta.kind === "plan-review";

  const sec = (re: RegExp) =>
    artifact.parsed.sections.find((s) => s.depth === 2 && re.test(s.heading));
  const killSection = sec(/^Steelman: kill/i);
  const pursueSection = sec(/^Steelman: pursue/i);
  const directives = sec(/^Revision directives/i);
  const extraSections = artifact.parsed.sections.filter(
    (s) =>
      s.depth === 2 &&
      !/^(Verdicts|Steelman|Revision directives|Re-verdicts|Gate \d|Plan verdict)/i.test(
        s.heading,
      ),
  );

  const shown =
    filter === "all"
      ? firstPass
      : firstPass.filter((v) => v.verdictNorm === filter);

  return (
    <div>
      {planVerdict && (
        <div
          className={`gate-banner ${/^APPROVE/i.test(planVerdict) ? "decided" : ""}`}
        >
          <h1>Plan verdict</h1>
          <div className="verdict-line">{planVerdict}</div>
        </div>
      )}

      <div className="stat-row">
        <StatTile value={firstPass.length} label="claims attacked" />
        <StatTile
          value={`${survival}%`}
          label="survived"
          tone={survival >= 80 ? "good" : survival >= 50 ? "warn" : "critical"}
        />
        <StatTile value={count("SUPPORTED")} label="held" tone="good" />
        <StatTile value={count("WEAK")} label="thin" tone="warn" />
        <StatTile value={count("REFUTED")} label="broken" tone="critical" />
        {reverdicts.length > 0 && (
          <StatTile value={reverdicts.length} label="re-verdicts after revision" />
        )}
      </div>

      {registry && (
        <p className="status-sentence">
          Registry check: <strong>{registry}</strong>
        </p>
      )}

      <div className="filter-row">
        {(
          [
            "all",
            "SUPPORTED",
            "WEAK",
            "REFUTED",
            "UNVERIFIABLE",
          ] as const
        ).map((f) => (
          <button
            key={f}
            className={`chip clickable ${filter === f ? "accent" : "ghost"}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? `All (${firstPass.length})` : f}
          </button>
        ))}
      </div>

      <VerdictTable verdicts={shown} />

      {(killSection || pursueSection) && (
        <>
          <h3 style={{ margin: "22px 0 10px" }}>{steelmanTitle}</h3>
          <div className="steelman-grid">
            {killSection && (
              <div className="panel kill">
                <h2>Kill</h2>
                <MarkdownView
                  text={sectionText(artifact.raw, killSection)}
                  collapsible
                />
              </div>
            )}
            {pursueSection && (
              <div className="panel pursue">
                <h2>Pursue</h2>
                <MarkdownView
                  text={sectionText(artifact.raw, pursueSection)}
                  collapsible
                />
              </div>
            )}
          </div>
        </>
      )}

      {directives && (
        <div className="panel" style={{ marginTop: 16 }}>
          <h2>{directives.heading}</h2>
          <MarkdownView text={sectionText(artifact.raw, directives)} collapsible />
        </div>
      )}

      {reverdicts.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3>Re-verdicts after revision (append-only)</h3>
          <VerdictTable verdicts={reverdicts} />
        </div>
      )}

      {extraSections.map((s) => (
        <details className="section-acc" key={s.heading}>
          <summary>{s.heading}</summary>
          <MarkdownView text={sectionText(artifact.raw, s)} collapsible />
        </details>
      ))}

      {isPass2 && artifact.parsed.gate && (
        <GateBox run={run} gate={2} />
      )}
    </div>
  );
}

function VerdictTable({ verdicts }: { verdicts: ParsedVerdict[] }) {
  return (
    <div className="table-wrap">
      <table className="verdicts">
        <thead>
          <tr>
            <th>ID</th>
            <th>Claim</th>
            <th>Verdict</th>
            <th>What the Skeptic checked</th>
          </tr>
        </thead>
        <tbody>
          {verdicts.map((v, i) => (
            <tr key={`${v.claimRef}-${i}`}>
              <td className="ref">{v.claimRef}</td>
              <td>{v.claimShort || <span style={{ color: "var(--muted)" }}>-</span>}</td>
              <td>
                <VerdictChip norm={v.verdictNorm} raw={v.verdictRaw} />
              </td>
              <td style={{ color: "var(--ink-2)" }}>
                <LongText text={v.justification} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- recommendation ----------

function RecommendationReader({
  artifact,
  run,
}: {
  artifact: ArtifactResponse;
  run: RunDetail;
}) {
  const verdict = artifact.parsed.meta["Verdict"] ?? "";
  const token = verdict.split(/[\s(]/)[0] ?? "";
  const sec = (re: RegExp) =>
    artifact.parsed.sections.find((s) => s.depth === 2 && re.test(s.heading));
  const plain = sec(/^In plain terms/i);
  const why = sec(/^Why/i);
  const killCheck = sec(/kill criteria/i);
  const evFor = sec(/evidence for/i);
  const evAgainst = sec(/evidence against/i);
  const unknowns = sec(/unknowns/i);

  return (
    <div>
      <div
        className={`gate-banner ${token === "PURSUE" ? "decided" : ""}`}
        style={
          token === "KILL"
            ? { borderColor: "var(--critical)", background: "var(--wash-critical)" }
            : undefined
        }
      >
        <h1>Verdict: {token || "recorded below"}</h1>
        <div className="verdict-line">{verdict.replace(/^[A-Z]+\s*/, "")}</div>
      </div>

      {plain && (
        <div className="panel" style={{ fontSize: 15.5 }}>
          <h2>In plain terms</h2>
          <MarkdownView text={sectionText(artifact.raw, plain)} />
        </div>
      )}

      {why && (
        <div className="panel">
          <h2>Why</h2>
          <MarkdownView text={sectionText(artifact.raw, why)} collapsible />
        </div>
      )}

      {killCheck && (
        <div className="panel" style={{ borderColor: "var(--warn)" }}>
          <h2>Deal-breaker check (registered before research - applied as written)</h2>
          <MarkdownView text={sectionText(artifact.raw, killCheck)} collapsible />
        </div>
      )}

      {(evFor || evAgainst) && (
        <div className="steelman-grid">
          {evFor && (
            <div className="panel pursue">
              <h2>Strongest evidence for</h2>
              <MarkdownView text={sectionText(artifact.raw, evFor)} collapsible />
            </div>
          )}
          {evAgainst && (
            <div className="panel kill">
              <h2>Strongest evidence against</h2>
              <MarkdownView text={sectionText(artifact.raw, evAgainst)} collapsible />
            </div>
          )}
        </div>
      )}

      {unknowns && (
        <div className="panel" style={{ marginTop: 16 }}>
          <h2>What we still don't know</h2>
          <MarkdownView text={sectionText(artifact.raw, unknowns)} collapsible />
        </div>
      )}

      <GateBox run={run} gate={1} />
    </div>
  );
}

// ---------- intake ----------

function IntakeReader({
  artifact,
  run,
}: {
  artifact: ArtifactResponse;
  run: RunDetail;
}) {
  const meta = artifact.parsed.meta;
  const { simple } = useVocab();
  const problem = artifact.parsed.sections.find((s) =>
    /^Problem statement/i.test(s.heading),
  );
  const rest = artifact.parsed.sections.filter(
    (s) => s.depth === 2 && !/^(Problem statement|Gate \d)/i.test(s.heading),
  );

  return (
    <div>
      <div className="filter-row">
        {Object.entries(meta).map(([k, v]) => (
          <span key={k} className="chip ghost" title={v}>
            {k}: {v.length > 40 ? `${v.slice(0, 40)}...` : v}
          </span>
        ))}
      </div>
      {problem && (
        <div className="panel" style={{ fontSize: 17 }}>
          <h2>The problem, as the run attacks it</h2>
          <MarkdownView text={sectionText(artifact.raw, problem)} />
        </div>
      )}
      {rest.map((s) => (
        <details className="section-acc" key={s.heading} open={!simple}>
          <summary>{s.heading}</summary>
          <MarkdownView text={sectionText(artifact.raw, s)} collapsible />
        </details>
      ))}
      <GateBox run={run} gate={0} />
    </div>
  );
}

// ---------- implementation plan ----------

function RoadmapReader({ artifact }: { artifact: ArtifactResponse }) {
  const { simple } = useVocab();
  const plain = artifact.parsed.sections.find(
    (s) => s.depth === 2 && /^In plain terms/i.test(s.heading),
  );
  const monday = artifact.parsed.sections.find((s) =>
    /monday morning/i.test(s.heading),
  );
  const rest = artifact.parsed.sections.filter(
    (s) => s.depth === 2 && s !== monday && s !== plain,
  );
  return (
    <div>
      {plain && (
        <div className="panel" style={{ fontSize: 15.5 }}>
          <h2>In plain terms</h2>
          <MarkdownView text={sectionText(artifact.raw, plain)} />
        </div>
      )}
      {monday && (
        <div className="panel" style={{ borderColor: "var(--accent)" }}>
          <h2>Monday morning - do these first</h2>
          <MarkdownView text={sectionText(artifact.raw, monday)} />
        </div>
      )}
      {rest.map((s) => (
        <details className="section-acc" key={s.heading} open={!simple}>
          <summary>{s.heading}</summary>
          <MarkdownView text={sectionText(artifact.raw, s)} collapsible />
        </details>
      ))}
    </div>
  );
}

// ---------- next steps ----------

/** Split a section into the prose before its first list item (or table)
 *  and the list items themselves, folding indented continuation lines into
 *  their item. Nothing may be dropped: prose keeps rendering as markdown. */
function splitListSection(text: string): { intro: string; items: string[] } {
  const introLines: string[] = [];
  const items: string[] = [];
  for (const line of text.split("\n")) {
    const start = /^\s*(?:\d+[.)]|[-*])\s+(.+)$/.exec(line);
    if (start) {
      items.push(start[1]!.trim());
    } else if (items.length > 0 && /^\s+\S/.test(line)) {
      items[items.length - 1] += ` ${line.trim()}`;
    } else if (items.length === 0) {
      introLines.push(line);
    }
  }
  return {
    intro: introLines.join("\n").trim(),
    items: items.filter((x) => x.length > 0),
  };
}

/** A table cell or list item is an inline fragment: escape a leading block
 *  marker so ">=4,000" or "# of calls" is not rendered as a quote or heading. */
function inlineFragment(text: string): string {
  return text.replace(/^(\s*)(>|#{1,6}\s|[-+*]\s|\d+[.)]\s)/, "$1\\$2");
}

interface MilestoneRow {
  cells: string[];
}

/** Parse a GFM table out of raw section text: header + data rows. */
function parseTable(text: string): { header: string[]; rows: MilestoneRow[] } | null {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("|"));
  if (lines.length < 3) return null;
  const split = (l: string) =>
    l
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());
  const header = split(lines[0]!);
  if (!/^[:\s|-]+$/.test(lines[1]!)) return null;
  const rows = lines
    .slice(2)
    .map((l) => ({ cells: split(l) }))
    .filter((r) => r.cells.some((c) => c.length > 0));
  if (rows.length === 0) return null;
  return { header, rows };
}

/** First money-ish figure in a budget bullet ("$1,500", "$2k-4k", "80 hrs"). */
function headlineFigure(text: string): string | null {
  const m = /\$[\d,.]+(?:[km]\b)?(?:\s*-\s*\$?[\d,.]+(?:[km]\b)?)?/i.exec(text);
  return m?.[0]?.trim() ?? null;
}

function NextStepsReader({
  artifact,
  run,
}: {
  artifact: ArtifactResponse;
  run: RunDetail;
}) {
  // Print mode: the stylesheet strips the app chrome while this page is
  // mounted so browser print-to-PDF yields a clean document.
  useEffect(() => {
    document.documentElement.classList.add("print-next-steps");
    return () => document.documentElement.classList.remove("print-next-steps");
  }, []);

  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const { ownerMode } = useVocab();

  const verdict = artifact.parsed.meta["Verdict"] ?? "";
  const token = verdict.split(/[\s(]/)[0] ?? "";
  const gateTrail = artifact.parsed.meta["Gate trail"];

  const sec = (re: RegExp) =>
    artifact.parsed.sections.find((s) => s.depth === 2 && re.test(s.heading));
  const story = sec(/^The story/i);
  const firstWeek = sec(/^First week/i);
  const milestones = sec(/^Milestones/i);
  const budget = sec(/^Budget/i);
  const killCriteria = sec(/^Kill criteria/i);
  const risks = sec(/^Risks accepted/i);
  const known = [story, firstWeek, milestones, budget, killCriteria, risks];
  const extras = artifact.parsed.sections.filter(
    (s) =>
      s.depth === 2 &&
      !known.includes(s) &&
      !/^Where the detail lives/i.test(s.heading),
  );

  const week = firstWeek
    ? splitListSection(sectionText(artifact.raw, firstWeek))
    : { intro: "", items: [] };
  const milestoneText = milestones ? sectionText(artifact.raw, milestones) : "";
  const table = milestones ? parseTable(milestoneText) : null;
  const milestoneIntro = milestoneText
    .split("\n")
    .filter((l) => !l.trim().startsWith("|"))
    .join("\n")
    .trim();
  const budgetLines = budget
    ? splitListSection(sectionText(artifact.raw, budget)).items
    : [];
  const riskSection = risks
    ? splitListSection(sectionText(artifact.raw, risks))
    : { intro: "", items: [] };

  // Column lookups by header name so the template can evolve its order.
  const col = (re: RegExp) =>
    table ? table.header.findIndex((h) => re.test(h)) : -1;
  const cMilestone = col(/milestone/i);
  const cDone = col(/done|acceptance/i);
  const cEffort = col(/effort/i);
  const cCost = col(/cost/i);

  const detailKinds = [
    "recommendation",
    "implementation-plan",
    "plan-review",
    "residue",
  ] as const;
  const hasPlan = run.artifacts.some((a) => a.kind === "implementation-plan");
  const detailLinks = detailKinds
    .map((k) => run.artifacts.find((a) => a.kind === k))
    .filter((a): a is NonNullable<typeof a> => a !== undefined);

  return (
    <div className="next-steps-page">
      <div
        className={`gate-banner ${token === "PURSUE" ? "decided" : ""}`}
        style={
          token === "KILL"
            ? { borderColor: "var(--critical)", background: "var(--wash-critical)" }
            : undefined
        }
      >
        <h1>Verdict: {token || "recorded below"}</h1>
        <div className="verdict-line">{verdict.replace(/^[A-Z]+\s*/, "")}</div>
        {gateTrail && (
          <div style={{ marginTop: 6, color: "var(--ink-2)", fontSize: 13.5 }}>
            Gate trail: {gateTrail}
          </div>
        )}
        <div className="ns-actions no-print">
          <a
            className="btn secondary"
            href={`/api/runs/${encodeURIComponent(run.id)}/export`}
            download
          >
            Download the deliverable (.zip)
          </a>
          <button className="btn secondary" onClick={() => window.print()}>
            Print / save as PDF
          </button>
        </div>
      </div>

      {story && (
        <div className="panel" style={{ fontSize: 15.5 }}>
          <h2>{story.heading}</h2>
          <MarkdownView text={sectionText(artifact.raw, story)} />
        </div>
      )}

      {firstWeek && (
        <div className="panel" style={{ borderColor: "var(--accent)" }}>
          {/* Only a run with a blueprint has a build to start; on a killed or
              paused run these are re-entry steps, so keep the file's heading. */}
          <h2>{hasPlan ? "First week - do these first" : firstWeek.heading}</h2>
          {week.intro && <MarkdownView text={week.intro} />}
          {week.items.length > 0 ? (
            <ol className="ns-checklist">
              {week.items.map((a, i) => (
                <li key={i} className={checked[i] ? "done" : ""}>
                  <label>
                    <input
                      type="checkbox"
                      checked={checked[i] ?? false}
                      onChange={() =>
                        setChecked((prev) => ({ ...prev, [i]: !prev[i] }))
                      }
                    />
                    <span className="md-inline">
                      <MarkdownView text={inlineFragment(a)} />
                    </span>
                  </label>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      )}

      {milestones && (
        <div className="panel">
          <h2>Milestones</h2>
          {table && cMilestone >= 0 && milestoneIntro && (
            <MarkdownView text={milestoneIntro} />
          )}
          {table && cMilestone >= 0 ? (
            <ol className="ns-timeline">
              {table.rows.map((r, i) => (
                <li key={i}>
                  <div className="ns-milestone-head">
                    <strong className="md-inline">
                      <MarkdownView
                        text={inlineFragment(`${r.cells[0] && cMilestone !== 0 ? `${r.cells[0]} - ` : ""}${r.cells[cMilestone] ?? ""}`)}
                      />
                    </strong>
                    {cEffort >= 0 && r.cells[cEffort] && r.cells[cEffort] !== "-" && (
                      <span className="chip ghost">effort {r.cells[cEffort]}</span>
                    )}
                    {cCost >= 0 && r.cells[cCost] && r.cells[cCost] !== "-" && (
                      <span className="chip ghost">{r.cells[cCost]}</span>
                    )}
                  </div>
                  {cDone >= 0 && r.cells[cDone] && (
                    <div className="ns-milestone-done md-inline">
                      Done when: <MarkdownView text={inlineFragment(r.cells[cDone])} />
                    </div>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <MarkdownView text={sectionText(artifact.raw, milestones)} collapsible />
          )}
        </div>
      )}

      {budget && (
        <div className="panel">
          <h2>Budget</h2>
          {budgetLines.some((l) => headlineFigure(l)) && (
            <div className="stat-row">
              {budgetLines.map((l, i) => {
                const fig = headlineFigure(l);
                if (!fig) return null;
                const label = l.split(":")[0]?.replace(/\*\*/g, "").trim() ?? "";
                return <StatTile key={i} value={fig} label={label} />;
              })}
            </div>
          )}
          <MarkdownView text={sectionText(artifact.raw, budget)} />
        </div>
      )}

      {killCriteria && (
        <div className="panel" style={{ borderColor: "var(--warn)" }}>
          <h2>{killCriteria.heading}</h2>
          <MarkdownView text={sectionText(artifact.raw, killCriteria)} />
        </div>
      )}

      {risks && (
        <div className="panel ns-risks">
          <h2>Risks you accepted at the gates</h2>
          {riskSection.intro && <MarkdownView text={riskSection.intro} />}
          {riskSection.items.length > 0 ? (
            <ul className="ns-risk-list">
              {riskSection.items.map((r, i) => (
                <li key={i}>
                  <span className="md-inline">
                    <MarkdownView text={r} />
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {extras.map((s) => (
        <details className="section-acc" key={s.heading}>
          <summary>{s.heading}</summary>
          <MarkdownView text={sectionText(artifact.raw, s)} collapsible />
        </details>
      ))}

      {detailLinks.length > 0 && (
        <div className="panel no-print">
          <h2>Where the detail lives</h2>
          <div className="filter-row" style={{ marginBottom: 0 }}>
            {detailLinks.map((a) => (
              <Link
                key={a.relPath}
                className="chip ghost clickable"
                to={`/runs/${run.id}/a/${a.relPath}`}
              >
                {(ownerMode
                  ? KIND_TITLES[a.kind]?.owner
                  : KIND_TITLES[a.kind]?.user) ?? a.relPath}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- debrief / residue ----------

// The debrief is the run's terminal page, so it carries the take-with-you
// downloads: the full committed paper trail (every markdown the run holds)
// and the condensed deliverable set that also lives on the next-steps page.
function DebriefReader({
  artifact,
  run,
}: {
  artifact: ArtifactResponse;
  run: RunDetail;
}) {
  return (
    <div>
      <div
        className="panel no-print"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
          Take this run with you - every markdown it produced, zipped:
        </span>
        <a
          className="btn secondary"
          href={`/api/runs/${encodeURIComponent(run.id)}/export?scope=all`}
          download
        >
          Download the full run (.zip)
        </a>
        <a
          className="btn secondary"
          href={`/api/runs/${encodeURIComponent(run.id)}/export`}
          download
        >
          Deliverable only (.zip)
        </a>
      </div>
      <div className="panel">
        <MarkdownView text={artifact.raw} />
      </div>
    </div>
  );
}

// ---------- shared gate box ----------

function GateBox({ run, gate }: { run: RunDetail; gate: 0 | 1 | 2 }) {
  const { gateTitle } = useVocab();
  const info = run.gates.find((g) => g.gate === gate);
  if (!info) return null;
  return (
    <div className={`gate-banner ${info.pending ? "" : "decided"}`} style={{ marginTop: 16 }}>
      <h1>{gateTitle(gate)}</h1>
      {info.pending ? (
        <div className="verdict-line">
          Waiting on you. <Link to={`/runs/${run.id}/gate/${gate}`}>Decide now</Link>
        </div>
      ) : (
        <div className="verdict-line">
          <strong>Decision:</strong> {info.decisionRaw}
          {info.decidedBy && (
            <>
              {" "}
              · <strong>by:</strong> {info.decidedBy}
            </>
          )}
          {info.rationale && (
            <div style={{ marginTop: 4, color: "var(--ink-2)" }}>
              <LongText text={info.rationale} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
